<template>
  <div class="metrics-header">
    <eg-button
      v-if="backPath"
      type="ordinary"
      icon="arrow-left"
      icon-size="medium"
      class="metrics-header__back-btn"
      :aria-label="$t('metrics.backToMetrics')"
      @click="$router.push(backPath)"
    />
    <div class="metrics-header__title-group">
      <h1 class="metrics-header__title">
        <template v-if="title">
          {{ title }}
          <span v-if="isDemoMode" class="metrics-header__demo-label"
            >(Demo)</span
          >
        </template>
        <template v-else>
          {{
            isDemoMode
              ? $t("metrics.overviewTitleDemo")
              : $t("metrics.overviewTitle")
          }}
        </template>
      </h1>
      <eg-toggle
        v-if="!hideToggle"
        :value="isDemoMode"
        :label="
          isDemoMode
            ? $t('metrics.switchToRealData')
            : $t('metrics.switchToDemoData')
        "
        class="metrics-header__demo-toggle"
        @input="$emit('update:isDemoMode', $event)"
      />
    </div>

    <div v-if="!hideControls" class="metrics-header__controls no-print">
      <eg-dropdown
        ref="dropdown"
        :text="selectedTimeFilterLabel"
        class="metrics-header__time-filter"
      >
        <eg-b-dropdown-item-button
          v-for="option in timeFilterOptions"
          :key="option.value"
          @click="$emit('update:timeFilter', option.value)"
        >
          {{ option.label }}
        </eg-b-dropdown-item-button>
      </eg-dropdown>
      <div class="divider m-0 ml-2"></div>
      <eg-button
        type="primary"
        icon="plus-add-more-maximize"
        icon-size="medium"
        @click="$emit('create-report')"
      >
        {{ $t("metrics.createReport") }}
      </eg-button>
      <eg-dropdown right class="metrics-header__more-dropdown">
        <template #button-content>
          <eg-icon icon="more-options-vertical-dots-dots" size="medium" />
        </template>
        <eg-b-dropdown-item @click="$emit('export-pdf')">
          <eg-icon icon="file-download" size="medium" />
          {{ $t("metrics.exportPdf") }}
        </eg-b-dropdown-item>
        <eg-b-dropdown-item @click="$emit('print')">
          <eg-icon icon="print" size="medium" />
          {{ $t("metrics.print") }}
        </eg-b-dropdown-item>
      </eg-dropdown>
    </div>
  </div>
</template>

<script>
import { METRICS_TIME_FILTERS } from "../../demo_data/shared.js";
import dropdownHideOnLeaveMixin from "@/mixins/dropdownHideOnLeaveMixin";

export default {
  name: "MetricsHeader",

  mixins: [dropdownHideOnLeaveMixin],

  props: {
    timeFilter: {
      type: String,
      default: METRICS_TIME_FILTERS[0].value,
    },
    isDemoMode: {
      type: Boolean,
      default: false,
    },
    hideToggle: {
      type: Boolean,
      default: false,
    },
    hideControls: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: null,
    },
    backPath: {
      type: String,
      default: null,
    },
  },

  computed: {
    timeFilterOptions() {
      return METRICS_TIME_FILTERS.map((o) => ({
        ...o,
        label: this.$t(o.labelKey),
      }));
    },

    selectedTimeFilterLabel() {
      const found = this.timeFilterOptions.find(
        (o) => o.value === this.timeFilter
      );
      return found ? found.label : "";
    },
  },
};
</script>

<style lang="scss" scoped>
@import "metrics_card";

.metrics-header {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  padding: 16px 24px;
  background: var(--color-surface-neutral-background, #fff);
  border-bottom: 1px solid var(--color-stroke-neutral-default, #e5ebf0);

  &__title-group {
    display: flex;
    align-items: center;
    gap: 16px;
    justify-content: space-between;
    flex: 1;
  }

  &__title {
    color: var(--color-text-neutral-on-white, #1a2330);
    font-family: var(--typography-font-family-text-primary, Inter);
    font-size: 24px;
    font-weight: var(--typography-font-weight-fw-700, 700);
    line-height: 120%;
    margin: 0;
    white-space: nowrap;
  }

  &__demo-label {
    color: var(--color-text-neutral-fade);
    font-weight: var(--typography-font-weight-fw-500, 500);
  }

  &__demo-toggle {
    min-height: 20px;
    flex-shrink: 0;
  }

  &__controls {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-left: auto;
    flex-wrap: wrap;
    justify-content: flex-end;

    @media (max-width: 1024px) {
      gap: 6px;
    }
  }

  &__time-filter {
    min-width: 130px;
    @include metrics-dropdown;
    ::v-deep .btn {
      text-overflow: ellipsis;
    }
  }

  &__more-dropdown {
    @include metrics-dropdown-icon;
  }
  ::v-deep .eg-toggle {
    width: 190px;
    label {
      line-height: inherit;
      padding-left: 12px;
    }
  }
  .divider {
    margin: 0 8px;
    height: 31px;
    border-left: 1px solid var(--secondary);
  }
}
</style>
